import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Icon } from "@/components/icons";

export type ActionFeedbackState = {
  tone: "success" | "error" | "info";
  text: string;
};

const DISMISS_AFTER_MS = 3500;

const ToastContext = createContext<((feedback: ActionFeedbackState) => void) | null>(null);

/** Mounted once in AppLayout so every page shares one toast slot instead of
 *  each component owning its own auto-dismiss timer + fixed-position render. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);

  useEffect(() => {
    if (!feedback) return;
    const id = setTimeout(() => setFeedback(null), DISMISS_AFTER_MS);
    return () => clearTimeout(id);
  }, [feedback]);

  const showToast = useCallback((next: ActionFeedbackState) => setFeedback(next), []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {feedback && <ActionFeedback feedback={feedback} onDismiss={() => setFeedback(null)} />}
    </ToastContext.Provider>
  );
}

/** Fire a one-off toast in the shared slot ToastProvider renders for the whole app. */
export function useToast(): (feedback: ActionFeedbackState) => void {
  const showToast = useContext(ToastContext);
  if (!showToast) throw new Error("useToast must be used within a ToastProvider");
  return showToast;
}

/** Lightweight self-contained toast for one-off action feedback (react-toastify
 *  is planned but not yet wired — see docs/frontend-plan). Auto-dismiss is
 *  handled by ToastProvider; this component only renders. */
export function ActionFeedback({
  feedback,
  onDismiss,
}: {
  feedback: ActionFeedbackState;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "fixed bottom-4 right-4 z-[1500] flex max-w-[360px] items-center gap-2 rounded-[4px] border px-3 py-2 text-small font-medium shadow-soft",
        feedback.tone === "success"
          ? "border-green-400 bg-green-100 text-green-700"
          : feedback.tone === "info"
            ? "border-blue-400 bg-blue-100 text-blue-700"
            : "border-red-500 bg-white text-red-600",
      ].join(" ")}
    >
      <Icon
        name={
          feedback.tone === "success" ? "check_circle" : feedback.tone === "info" ? "info" : "error"
        }
        size={20}
        className="shrink-0"
      />
      <span className="min-w-0 flex-1">{feedback.text}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 text-grey-500 transition-colors hover:text-black"
      >
        <Icon name="close" size={20} />
      </button>
    </div>
  );
}
